import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';

export interface DebugStats {
  drawCalls: number;
  hexCount: number;
  totalHexes: number;
  activeClusters: number;
  maxClusters: number;
  stressRoutes: number;
  stressAvailable: boolean;
  fogAvailable: boolean;
}

export class DebugHUD implements GameModule {
  readonly name = 'DebugHUD';
  readonly stats: DebugStats = { drawCalls: 0, hexCount: 0, totalHexes: 0, activeClusters: 0, maxClusters: 0, stressRoutes: 0, stressAvailable: false, fogAvailable: false };

  private _el!: HTMLDivElement;
  private _engine!: Engine;
  private _timer = 0;
  private _visible: boolean;

  constructor(startVisible = true) {
    this._visible = startVisible;
  }
  private _onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      this._visible = !this._visible;
      this._el.style.display = this._visible ? '' : 'none';
      this._engine.events.emit('debug-hud:toggled', this._visible);
    }
  };

  private _fpsEl!: HTMLSpanElement;
  private _msEl!: HTMLSpanElement;
  private _drawEl!: HTMLSpanElement;
  private _clusterEl!: HTMLSpanElement;
  private _memEl!: HTMLSpanElement;
  private _gpuEl!: HTMLSpanElement;

  private static readonly LONG_PRESS_MS = 5000;
  private static readonly MOVE_THRESHOLD_SQ = 2500; // 50px — generous for mobile jitter
  private _longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private _longPressStartX = 0;
  private _longPressStartY = 0;

  private _onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== 1 || this._longPressTimer !== null) return;
    const t = e.touches[0];
    this._longPressStartX = t.clientX;
    this._longPressStartY = t.clientY;
    this._longPressTimer = setTimeout(() => {
      this._longPressTimer = null;
      this._visible = !this._visible;
      this._el.style.display = this._visible ? '' : 'none';
      this._engine.events.emit('debug-hud:toggled', this._visible);
    }, DebugHUD.LONG_PRESS_MS);
  };

  private _onTouchMove = (e: TouchEvent): void => {
    if (this._longPressTimer === null || !e.touches[0]) return;
    const dx = e.touches[0].clientX - this._longPressStartX;
    const dy = e.touches[0].clientY - this._longPressStartY;
    if (dx * dx + dy * dy > DebugHUD.MOVE_THRESHOLD_SQ) {
      clearTimeout(this._longPressTimer);
      this._longPressTimer = null;
    }
  };

  private _onTouchEnd = (): void => {
    if (this._longPressTimer !== null) {
      clearTimeout(this._longPressTimer);
      this._longPressTimer = null;
    }
  };

  init(engine: Engine): void {
    this._engine = engine;

    this._el = document.createElement('div');
    Object.assign(this._el.style, {
      position: 'fixed', top: '8px', left: '8px', color: '#0f0',
      fontFamily: 'monospace', fontSize: 'clamp(0.6rem,1.8vw,0.75rem)', lineHeight: '1.6',
      background: 'rgba(0,0,0,0.6)', padding: 'clamp(4px,1vw,6px) clamp(6px,1.5vw,10px)',
      borderRadius: '4px', zIndex: '10000', pointerEvents: 'none',
      transition: 'top 0.3s, bottom 0.3s',
      display: this._visible ? '' : 'none',
    });

    engine.events.on('planet:hub-opened', () => {
      this._el.style.top = 'auto';
      this._el.style.bottom = '8px';
    });
    engine.events.on('planet:hub-closed', () => {
      this._el.style.bottom = 'auto';
      this._el.style.top = '8px';
    });

    this._fpsEl = this._span('#0f0');
    this._msEl = this._span('#0f0');
    this._drawEl = this._span('#0f0');
    this._clusterEl = this._span('#0f0');
    this._memEl = this._span('#0f0');
    this._gpuEl = this._span('#888');
    this._gpuEl.style.fontSize = '10px';

    this._el.append(
      this._fpsEl, ' · ', this._msEl, '\n',
      this._drawEl, '\n', this._clusterEl, '\n',
      this._memEl, '\n', this._gpuEl,
    );
    this._el.style.whiteSpace = 'pre';

    const ext = engine.gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) {
      this._gpuEl.textContent = engine.gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
    }
    document.body.appendChild(this._el);
    document.addEventListener('keydown', this._onKey, { capture: true });

    const canvas = engine.canvas;
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this._onTouchMove, { passive: true });
    canvas.addEventListener('touchend', this._onTouchEnd);
    canvas.addEventListener('touchcancel', this._onTouchEnd);

    if (!this._visible) engine.events.emit('debug-hud:toggled', false);
  }

  update(dt: number): void {
    this._timer += dt;
    if (this._timer < 0.5) return;
    this._timer = 0;

    const clock = this._engine.clock;
    const fps = clock.fps;
    const s = this.stats;

    this._fpsEl.style.color = fps >= 55 ? '#0f0' : fps >= 30 ? '#ff0' : '#f00';
    this._fpsEl.textContent = (fps | 0) + ' FPS';
    this._msEl.textContent = fps > 0 ? (1000 / fps).toFixed(1) + ' ms' : '— ms';
    this._drawEl.textContent = 'DRAW ' + s.drawCalls
      + ' · HEX ' + (s.hexCount / 1000).toFixed(1) + 'k / '
      + (s.totalHexes / 1_000_000).toFixed(1) + 'M';
    let clusterText = 'CLUSTERS ' + s.activeClusters + '/' + s.maxClusters;
    if (s.stressRoutes > 0) clusterText += ' · STRESS ' + s.stressRoutes + ' routes';
    else if (s.stressAvailable) clusterText += ' · Ctrl+Shift+S=Stress';
    if (s.fogAvailable) clusterText += ' · Ctrl+Shift+F=Fog';
    this._clusterEl.textContent = clusterText;

    const perf = performance as unknown as { memory?: { usedJSHeapSize: number } };
    this._memEl.textContent = perf.memory
      ? 'MEM  ' + (perf.memory.usedJSHeapSize / 1048576).toFixed(1) + ' MB'
      : '';
  }

  dispose(): void {
    document.removeEventListener('keydown', this._onKey, { capture: true });
    const canvas = this._engine.canvas;
    canvas.removeEventListener('touchstart', this._onTouchStart);
    canvas.removeEventListener('touchmove', this._onTouchMove);
    canvas.removeEventListener('touchend', this._onTouchEnd);
    canvas.removeEventListener('touchcancel', this._onTouchEnd);
    if (this._longPressTimer !== null) {
      clearTimeout(this._longPressTimer);
      this._longPressTimer = null;
    }
    this._el.remove();
  }

  private _span(color: string): HTMLSpanElement {
    const el = document.createElement('span');
    el.style.color = color;
    return el;
  }
}
